import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { TwoLevelNav } from '@/components/Navigation'
import { useAuth, ClientProjectProvider } from '@/contexts'
import { useContractUploadNotifications } from '@/hooks/useContractUploadNotifications'
import '@/layouts/AppLayout.css'

interface AppSearchParams {
  clientId?: string
  projectId?: string
}

export const Route = createFileRoute('/_app')({
  component: AppLayout,
  validateSearch: (search: Record<string, unknown>): AppSearchParams => ({
    clientId: typeof search.clientId === 'string' ? search.clientId : undefined,
    projectId: typeof search.projectId === 'string' ? search.projectId : undefined,
  }),
})

function AppLayout() {
  const { isAuthenticated, isLoading, connectionLost, retrySession, logout } = useAuth()
  const navigate = useNavigate()

  // Listen for bulk contract-upload progress and toast on start/completion.
  useContractUploadNotifications()

  // Only a CONFIRMED sign-out leaves the workbench; an unreachable server
  // shows the connection-lost state below instead of ejecting the reviewer.
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !connectionLost) {
      navigate({ to: '/' })
    }
  }, [isAuthenticated, isLoading, connectionLost, navigate])

  if (connectionLost && !isAuthenticated) {
    return (
      <div className="app-shell-state">
        <div className="app-shell-card" role="alert">
          <h1>Connection lost</h1>
          <p>
            Aerris can't reach the server right now. Your work is safe on the
            server; nothing has been signed out or discarded.
          </p>
          <div className="app-shell-actions">
            <button className="app-shell-retry" onClick={retrySession}>
              Try again
            </button>
            <button className="app-shell-secondary" onClick={() => logout()}>
              Back to sign-in
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Session confirmed signed-out — the effect above is redirecting to '/'; render nothing meanwhile.
  if (!isLoading && !isAuthenticated) {
    return null
  }

  // Optimistic render: while the session is still being confirmed (isLoading), show the real
  // workbench — the nav is null-safe on the user and each page owns its loading state — rather than
  // a full-screen "Checking your session…" gate. A signed-in reviewer (a refresh or a return visit)
  // sees the app immediately; a stale session surfaces as the child data calls 401ing (which bounce
  // to '/') or as the /auth/me 403 tripping the redirect effect above.
  return (
    <ClientProjectProvider>
      <div className="app-layout">
        <TwoLevelNav />
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </ClientProjectProvider>
  )
}
